import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { coachAPI, adaptiveAPI } from '../../services/api';
import CoachLayout from './CoachLayout';

/* ── Card Modal (create + edit) ───────────────────────────────────────────── */
function CardModal({ domain, skillId, card, onSave, onClose }) {
  const [form, setForm] = useState({
    front: card?.front || '',
    back: card?.back || '',
    key_insight: card?.key_insight || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.front.trim() || !form.back.trim()) {
      setError('Front and back text are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (card) {
        const sceneId = card.scene_id || card._id;
        await adaptiveAPI.updateReviewCard(domain, skillId, sceneId, form);
      } else {
        await adaptiveAPI.createReviewCard(domain, skillId, form);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <h3 className="coach-modal-title">{card ? 'Edit Flashcard' : 'New Flashcard'}</h3>
        <div className="coach-form-group">
          <label className="coach-label">Front (question / prompt) *</label>
          <textarea
            className="coach-textarea"
            value={form.front}
            onChange={set('front')}
            rows={3}
            placeholder="What does Cover 2 mean?"
            autoFocus
          />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Back (answer / explanation) *</label>
          <textarea
            className="coach-textarea"
            value={form.back}
            onChange={set('back')}
            rows={4}
            placeholder="Cover 2 is a zone defense where two safeties split the deep field in half…"
          />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Key Insight (optional badge text)</label>
          <input
            className="coach-input"
            value={form.key_insight}
            onChange={set('key_insight')}
            placeholder="e.g. Zone concept"
          />
          <span className="text-muted text-sm">Shown as a badge on the card. Keep it brief.</span>
        </div>
        {error && <div className="coach-error" style={{ marginBottom: 12 }}>⚠ {error}</div>}
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : card ? 'Update Card' : 'Create Card'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Flashcard tile ────────────────────────────────────────────────────────── */
function FlashCard({ card, onEdit, onDelete }) {
  const sceneId = card.scene_id || card._id;
  const frontPreview = (card.front || '').slice(0, 100);

  return (
    <div
      data-testid={`review-card-${sceneId}`}
      style={{
        background: 'var(--coach-card-bg, rgba(30,41,59,0.8))',
        border: '1px solid var(--coach-border, rgba(255,255,255,0.08))',
        borderRadius: 10,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 120,
      }}
    >
      {/* Key insight badge */}
      {card.key_insight && (
        <div>
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 999,
            fontSize: '0.72rem',
            fontWeight: 700,
            background: 'rgba(99,102,241,0.2)',
            color: '#a78bfa',
            border: '1px solid rgba(99,102,241,0.3)',
          }}>
            {card.key_insight}
          </span>
        </div>
      )}

      {/* Front text */}
      <div style={{ flex: 1, fontSize: '0.85rem', color: '#e2e8f0', lineHeight: 1.5 }}>
        {frontPreview}
        {(card.front || '').length > 100 && <span style={{ color: '#475569' }}> …</span>}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 'auto' }}>
        <button
          className="btn btn-outline btn-sm"
          data-testid={`edit-card-${sceneId}`}
          onClick={() => onEdit(card)}
          style={{ fontSize: '0.75rem', padding: '2px 10px' }}
        >
          Edit
        </button>
        <button
          className="btn btn-outline btn-sm"
          data-testid={`delete-card-${sceneId}`}
          onClick={() => onDelete(card)}
          style={{ fontSize: '0.75rem', padding: '2px 10px', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function ReviewCardLibraryPage() {
  const { domain: urlDomain } = useParams();
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(urlDomain || '');
  const [skills, setSkills] = useState([]);
  const [selectedSkillId, setSelectedSkillId] = useState('');
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);

  // Load domains; pre-select URL domain if present, else first returned
  useEffect(() => {
    coachAPI.listMyDomains()
      .then(({ data }) => {
        const raw = data?.domains;
        let list = [];
        if (Array.isArray(raw)) {
          list = raw.map((d) => (typeof d === 'string' ? d : d?.domain)).filter(Boolean);
        } else if (raw && typeof raw === 'object') {
          list = Object.keys(raw);
        }
        if (list.length) {
          setDomains(list);
          setSelectedDomain((prev) => {
            if (prev && list.includes(prev)) return prev;
            return urlDomain && list.includes(urlDomain) ? urlDomain : list[0];
          });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load skill stats for skill selector
  useEffect(() => {
    if (!selectedDomain) return;
    setSkillsLoading(true);
    setSelectedSkillId('');
    setCards([]);
    adaptiveAPI.getItemBankStats(selectedDomain)
      .then(({ data }) => {
        const list = data?.skills || data?.stats || [];
        setSkills(list);
        if (list.length) setSelectedSkillId(list[0]?.skill_id || list[0]?.id || '');
      })
      .catch(() => setSkills([]))
      .finally(() => setSkillsLoading(false));
  }, [selectedDomain]);

  // Load cards
  const loadCards = useCallback(() => {
    if (!selectedDomain || !selectedSkillId) return;
    setCardsLoading(true);
    adaptiveAPI.getReviewCardLibrary(selectedDomain, selectedSkillId)
      .then(({ data }) => setCards(data?.cards || data?.scenes || data || []))
      .catch(() => setCards([]))
      .finally(() => setCardsLoading(false));
  }, [selectedDomain, selectedSkillId]);

  useEffect(() => {
    setCards([]);
    loadCards();
  }, [loadCards]);

  const handleDelete = async (card) => {
    const sceneId = card.scene_id || card._id;
    if (!window.confirm('Delete this flashcard? This cannot be undone.')) return;
    try {
      await adaptiveAPI.deleteReviewCard(selectedDomain, selectedSkillId, sceneId);
      loadCards();
    } catch {
      alert('Delete failed.');
    }
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setShowModal(true);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingCard(null);
    loadCards();
  };

  return (
    <CoachLayout title="Review Card Library">
      {showModal && (
        <CardModal
          domain={selectedDomain}
          skillId={selectedSkillId}
          card={editingCard}
          onSave={handleModalSave}
          onClose={() => { setShowModal(false); setEditingCard(null); }}
        />
      )}

      <div className="coach-section">
        {/* ── Header controls ──────────────────────────────────────────────── */}
        <div className="coach-section-header" style={{ flexWrap: 'wrap', gap: 10 }}>
          <h2 className="coach-section-title">Flashcard Library</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              className="coach-select"
              style={{ width: 180 }}
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              data-testid="rc-domain-selector"
            >
              {domains.map((d) => (
                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1).replace(/_/g, ' ')}</option>
              ))}
            </select>

            {skillsLoading ? (
              <span className="text-muted text-sm">Loading skills…</span>
            ) : (
              <select
                className="coach-select"
                style={{ width: 220 }}
                value={selectedSkillId}
                onChange={(e) => setSelectedSkillId(e.target.value)}
                data-testid="rc-skill-selector"
                disabled={!skills.length}
              >
                {!skills.length && <option value="">No skills</option>}
                {skills.map((s) => {
                  const sid = s.skill_id || s.id;
                  return (
                    <option key={sid} value={sid}>
                      {s.label || s.display_name || sid.split('.').pop()}
                    </option>
                  );
                })}
              </select>
            )}

            <button
              className="btn btn-primary"
              data-testid="new-card-btn"
              onClick={() => { setEditingCard(null); setShowModal(true); }}
              disabled={!selectedSkillId}
            >
              + New Card
            </button>
          </div>
        </div>

        {/* ── Card grid ────────────────────────────────────────────────────── */}
        {cardsLoading ? (
          <div className="coach-loading">Loading cards…</div>
        ) : !selectedSkillId ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">🃏</span>
            <div className="coach-empty-title">Select a skill</div>
            <div className="coach-empty-desc">Choose a domain and skill above to view its flashcards.</div>
          </div>
        ) : cards.length === 0 ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">🃏</span>
            <div className="coach-empty-title">No flashcards yet for this skill.</div>
            <div className="coach-empty-desc">
              Generate some using the ScenarioArchitect agent or create them manually using <strong>+ New Card</strong>.
            </div>
          </div>
        ) : (
          <div
            data-testid="review-card-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 14,
              marginTop: 4,
            }}
          >
            {cards.map((card) => (
              <FlashCard
                key={card.scene_id || card._id}
                card={card}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </CoachLayout>
  );
}
